import type { MowerId } from '../../content/mowers';
import { Dir, type Direction } from '../../sim/direction';
import { paintMower3q, paintWreck3q, type PaintSlot, type WreckLayers } from './mower3q';

/**
 * Mower sprites: each mower drawn in three-quarter view (mower3q.ts: side, front and back; west
 * mirrors east) on a square frame centered on the anchor tile, 28 px for most (bigger machines
 * overhang the 16 px tile), 48 px for the 3-wide Fairway Mower. Each frame gets a crisp dark
 * outline and a soft shadow on the ground beneath it.
 */
const FRAME: Record<MowerId, number> = {
  reel: 28,
  push: 28,
  selfPropelled: 28,
  tractor: 28,
  zeroTurn: 28,
  fairway: 48,
};

/** A mower's sheet frame size (square, centered on its tile). */
export const mowerFrameSize = (id: MowerId): number => FRAME[id];

const OUTLINE = '#10180c';
const SHADOW = 'rgba(10, 50, 20, 0.36)';

export type Paint = readonly [body: string, light: string, dark: string];
/** Color for a paint pixel: slot 0 body, 1 light, 2 dark; x/y allow patterns (camo). */
export type PaintFn = (slot: PaintSlot, x: number, y: number) => string;

const PAINT: Record<MowerId, Paint> = {
  reel: ['#d8432f', '#f07a5f', '#a83222'],
  // The classic push mower wears the brand orange (it is the app icon).
  push: ['#e8862c', '#f6ad62', '#b4621a'],
  selfPropelled: ['#8e5bd6', '#b58ef0', '#6740a6'],
  tractor: ['#e8c547', '#f6df84', '#b8972a'],
  zeroTurn: ['#3a7bd5', '#74a6ea', '#28599e'],
  // Golf-course cream, so it stands out on the fairway.
  fairway: ['#e9e3cf', '#fbf8ee', '#a9a28a'],
};

export type PutPixel = (x: number, y: number, color: string) => void;

/**
 * Paints `mowerId` facing `dir` (frame coordinates, before outline and shadow). `broken` paints
 * the wreck instead (facing no way in particular; its ground and smoke go to `wreck` if given);
 * `paintFn` replaces the mower's own paint (cosmetics).
 */
export function paintMower(
  put: PutPixel,
  mowerId: MowerId,
  dir: Direction = Dir.E,
  broken = false,
  paintFn?: PaintFn,
  wreck?: WreckLayers,
): void {
  const own = PAINT[mowerId];
  const paint: PaintFn = paintFn ?? ((slot) => own[slot]);
  const size = FRAME[mowerId];
  if (broken) {
    const inFrame =
      (to: PutPixel): PutPixel =>
      (x, y, color) => {
        if (x >= 0 && y >= 0 && x < size && y < size) to(x, y, color);
      };
    const none: PutPixel = () => {};
    paintWreck3q(
      inFrame(put),
      { ground: inFrame(wreck?.ground ?? none), smoke: inFrame(wreck?.smoke ?? none) },
      mowerId,
      paint,
    );
    return;
  }
  const mirror = dir === Dir.W;
  paintMower3q(
    (x, y, color) => {
      if (x < 0 || y < 0 || x >= size || y >= size) return;
      put(mirror ? size - 1 - x : x, y, color);
    },
    mowerId,
    mirror ? Dir.E : dir,
    paint,
  );
}

const iconOffsets = new Map<string, { x: number; y: number }>();

/**
 * How far to shift a mower's frame to center its visible shape. A frame centers the deck on its
 * tile (right for the lawn), so a mower with a handle or a bag sits off-center as an icon; add
 * this (times the icon's scale) to its position.
 */
export function mowerIconOffset(
  mowerId: MowerId,
  dir: Direction = Dir.E,
): { x: number; y: number } {
  const key = `${mowerId}:${dir}`;
  const cached = iconOffsets.get(key);
  if (cached) return cached;
  let [x0, y0, x1, y1] = [Infinity, Infinity, -Infinity, -Infinity];
  paintMower(
    (x, y) => {
      x0 = Math.min(x0, x);
      y0 = Math.min(y0, y);
      x1 = Math.max(x1, x);
      y1 = Math.max(y1, y);
    },
    mowerId,
    dir,
  );
  const half = FRAME[mowerId] / 2;
  const offset = {
    x: Math.round(half - (x0 + x1 + 1) / 2),
    y: Math.round(half - (y0 + y1 + 1) / 2),
  };
  iconOffsets.set(key, offset);
  return offset;
}

/**
 * One sheet frame: the mower facing `dir`, with a 1 px dark outline and a soft shadow on the
 * ground beneath it. `put` takes frame coordinates (0..mowerFrameSize).
 */
export function drawMowerFrame(
  put: PutPixel,
  mowerId: MowerId,
  dir: Direction,
  broken = false,
  paintFn?: PaintFn,
): void {
  const frame = FRAME[mowerId];
  const pixels = new Map<number, string>();
  const ground = new Map<number, string>();
  const smoke = new Map<number, string>();
  const key = (x: number, y: number) => y * frame + x;
  paintMower((x, y, color) => pixels.set(key(x, y), color), mowerId, dir, broken, paintFn, {
    ground: (x, y, color) => ground.set(key(x, y), color),
    smoke: (x, y, color) => smoke.set(key(x, y), color),
  });
  const solid = (x: number, y: number) =>
    x >= 0 && y >= 0 && x < frame && y < frame && pixels.has(key(x, y));
  // The ground shadow: an ellipse under the mower, wider side on.
  const side = dir === Dir.E || dir === Dir.W;
  const cx = frame / 2;
  const cy = frame / 2 + frame * 0.2;
  const rx = frame * (side ? 0.4 : 0.34);
  const ry = frame * 0.09;
  for (let y = 0; y < frame; y++) {
    for (let x = 0; x < frame; x++) {
      if (solid(x, y)) continue;
      if (solid(x - 1, y) || solid(x + 1, y) || solid(x, y - 1) || solid(x, y + 1)) {
        put(x, y, OUTLINE);
        continue;
      }
      const ex = (x + 0.5 - cx) / rx;
      const ey = (y + 0.5 - cy) / ry;
      if (ex * ex + ey * ey <= 1) put(x, y, SHADOW);
    }
  }
  // A wreck's scorch and spills lie on the ground (under its outline); its smoke rises over it.
  for (const [k, color] of ground) if (!pixels.has(k)) put(k % frame, Math.floor(k / frame), color);
  for (const [k, color] of pixels) put(k % frame, Math.floor(k / frame), color);
  for (const [k, color] of smoke) put(k % frame, Math.floor(k / frame), color);
}
